import React, { useState, useEffect } from 'react';
import { Coach, CreateCoachRequest } from '../../types/auth';
import './CoachModal.css';

interface CoachModalProps {
  coach: Coach | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (coachData: CreateCoachRequest) => void;
}

const CoachModal: React.FC<CoachModalProps> = ({
  coach,
  isOpen,
  onClose,
  onSave,
}) => {
  const [rangePrompts, setRangePrompts] = useState<
    Array<{ min: string; max: string; prompt: string }>
  >([]);
  const [welcomeExperiments, setWelcomeExperiments] = useState<
    Array<{ prompt: string; message: string; bucket_list: string }>
  >([]);
  const [formData, setFormData] = useState<Partial<CreateCoachRequest>>({
    name: '',
    tagline: '',
    description: '',
    greeting: '',
    avatar_image_url: '',
    banner_image_url: '',
    nsfw: false,
    tags: {},
    temperature: 0.7,
    top_p: 1,
    top_k: 0,
    frequency_penalty: 0,
    presence_penalty: 0,
    repetition_penalty: 1,
    min_p: 0,
    top_a: 0,
    max_tokens: 150,
    language: 'english',
    model_name: 'gpt-3.5-turbo',
    attributes: {},
    gender: '',
    introduction: '',
    app_name: 'Quests App',
    visibility: 'PUBLIC',
    active: true,
    first_question: '',
    second_question: '',
    welcome_message: '',
    welcome_system_prompt: '',
    welcome_experiment: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (coach) {
      setFormData({
        name: coach.name || '',
        tagline: coach.tagline || '',
        description: coach.description || '',
        greeting: coach.greeting || '',
        avatar_image_url: coach.avatar_image_url || '',
        banner_image_url: coach.banner_image_url || '',
        nsfw: coach.nsfw || false,
        tags: coach.tags || {},
        temperature: coach.temperature || 0.7,
        top_p: coach.top_p || 1,
        top_k: coach.top_k || 0,
        frequency_penalty: coach.frequency_penalty || 0,
        presence_penalty: coach.presence_penalty || 0,
        repetition_penalty: coach.repetition_penalty || 1,
        min_p: coach.min_p || 0,
        top_a: coach.top_a || 0,
        max_tokens: coach.max_tokens || 150,
        language: coach.language || 'english',
        model_name: coach.model_name || 'gpt-3.5-turbo',
        attributes: coach.attributes || {},
        gender: coach.gender || '',
        introduction: coach.introduction || '',
        app_name: coach.app_name || 'Quests App',
        visibility: coach.visibility || 'PUBLIC',
        active: coach.active !== undefined ? coach.active : true,
        quest_id: coach.quest_id || '',
        first_question: coach.first_question || '',
        second_question: coach.second_question || '',
        welcome_message: (coach as any).welcome_message || '',
        welcome_system_prompt: (coach as any).welcome_system_prompt || '',
        welcome_experiment: (coach as any).welcome_experiment || [],
      });
      // Populate welcome experiments from API response
      if (
        (coach as any).welcome_experiment &&
        Array.isArray((coach as any).welcome_experiment)
      ) {
        setWelcomeExperiments(
          (coach as any).welcome_experiment.map((exp: any) => ({
            prompt: exp.prompt || '',
            message: exp.message || '',
            bucket_list: Array.isArray(exp.bucket_list)
              ? exp.bucket_list.join(', ')
              : '',
          }))
        );
      } else {
        setWelcomeExperiments([]);
      }
      // Populate range prompts from API response
      if ((coach as any).prompts && Array.isArray((coach as any).prompts)) {
        setRangePrompts(
          (coach as any).prompts.map((p: any) => ({
            min: p.lower_limit !== undefined ? String(p.lower_limit) : '',
            max: p.upper_limit !== undefined ? String(p.upper_limit) : '',
            prompt: p.prompt || '',
          }))
        );
      } else {
        setRangePrompts([]);
      }
    } else {
      setFormData({
        name: '',
        tagline: '',
        description: '',
        greeting: '',
        avatar_image_url: '',
        banner_image_url: '',
        nsfw: false,
        tags: {},
        temperature: 0.7,
        top_p: 1,
        top_k: 0,
        frequency_penalty: 0,
        presence_penalty: 0,
        repetition_penalty: 1,
        min_p: 0,
        top_a: 0,
        max_tokens: 150,
        language: 'english',
        model_name: 'gpt-3.5-turbo',
        attributes: {},
        gender: '',
        introduction: '',
        app_name: 'Quests App',
        visibility: 'PUBLIC',
        active: true,
        quest_id: '',
        first_question: '',
        second_question: '',
        welcome_message: '',
        welcome_system_prompt: '',
        welcome_experiment: [],
      });
      setRangePrompts([]);
      setWelcomeExperiments([]);
    }
    setErrors({});
  }, [coach]);

  const validateForm = (): boolean => {
    // No required field validations - all fields are optional
    setErrors({});
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const coachData: CreateCoachRequest = {
      name: formData.name || '',
      tagline: formData.tagline || '',
      description: formData.description || '',
      greeting: formData.greeting || '',
      avatar_image_url: formData.avatar_image_url || '',
      banner_image_url: formData.banner_image_url || '',
      nsfw: formData.nsfw || false,
      tags: formData.tags || {},
      temperature: formData.temperature || 0.7,
      top_p: formData.top_p || 1,
      top_k: formData.top_k || 0,
      frequency_penalty: formData.frequency_penalty || 0,
      presence_penalty: formData.presence_penalty || 0,
      repetition_penalty: formData.repetition_penalty || 1,
      min_p: formData.min_p || 0,
      top_a: formData.top_a || 0,
      max_tokens: formData.max_tokens || 150,
      language: formData.language || 'english',
      model_name: formData.model_name || 'gpt-3.5-turbo',
      attributes: formData.attributes || {},
      gender: formData.gender || '',
      introduction: formData.introduction || '',
      app_name: formData.app_name || 'Quests App',
      visibility: formData.visibility || 'PUBLIC',
      active: formData.active !== undefined ? formData.active : true,
      quest_id: formData.quest_id || '',
      first_question: formData.first_question || '',
      second_question: formData.second_question || '',
      welcome_message: formData.welcome_message || '',
      welcome_system_prompt: formData.welcome_system_prompt || '',
      prompts: rangePrompts.map(rp => ({
        lower_limit: rp.min,
        upper_limit: rp.max,
        prompt: rp.prompt,
      })),
      welcome_experiment: welcomeExperiments.map(exp => ({
        prompt: exp.prompt,
        message: exp.message,
        bucket_list: exp.bucket_list
          .split(',')
          .map(n => parseInt(n.trim()))
          .filter(n => !isNaN(n)),
      })),
    };

    onSave(coachData);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    let processedValue: any = value;

    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = parseFloat(value) || 0;
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddRangePrompt = () => {
    setRangePrompts(prev => [...prev, { min: '', max: '', prompt: '' }]);
  };

  const handleRangePromptChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setRangePrompts(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveRangePrompt = (index: number) => {
    setRangePrompts(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddWelcomeExperiment = () => {
    setWelcomeExperiments(prev => [
      ...prev,
      { prompt: '', message: '', bucket_list: '' },
    ]);
  };

  const handleWelcomeExperimentChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setWelcomeExperiments(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveWelcomeExperiment = (index: number) => {
    setWelcomeExperiments(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {coach ? 'Edit Coach' : 'Create Coach'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>

            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter coach name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tagline</label>
              <input
                type="text"
                name="tagline"
                className="form-input"
                value={formData.tagline}
                onChange={handleInputChange}
                placeholder="Enter a short tagline"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter coach description"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Greeting</label>
              <textarea
                name="greeting"
                className="form-textarea"
                value={formData.greeting}
                onChange={handleInputChange}
                placeholder="Enter initial greeting message"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Introduction</label>
              <textarea
                name="introduction"
                className="form-textarea"
                value={formData.introduction}
                onChange={handleInputChange}
                placeholder="Enter coach introduction"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label className="form-label">First Question</label>
              <input
                type="text"
                name="first_question"
                className="form-input"
                value={formData.first_question}
                onChange={handleInputChange}
                placeholder="Enter first question"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Second Question</label>
              <input
                type="text"
                name="second_question"
                className="form-input"
                value={formData.second_question}
                onChange={handleInputChange}
                placeholder="Enter second question"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Welcome Message</label>
              <textarea
                name="welcome_message"
                className="form-textarea"
                value={formData.welcome_message}
                onChange={handleInputChange}
                placeholder="Enter welcome message"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Welcome System Prompt</label>
              <textarea
                name="welcome_system_prompt"
                className="form-textarea"
                value={formData.welcome_system_prompt}
                onChange={handleInputChange}
                placeholder="Enter welcome system prompt"
                rows={4}
              />
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3 className="section-title">Images</h3>

            <div className="form-group">
              <label className="form-label">Avatar Image URL</label>
              <input
                type="url"
                name="avatar_image_url"
                className="form-input"
                value={formData.avatar_image_url}
                onChange={handleInputChange}
                placeholder="Enter avatar image URL"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Banner Image URL</label>
              <input
                type="url"
                name="banner_image_url"
                className="form-input"
                value={formData.banner_image_url}
                onChange={handleInputChange}
                placeholder="Enter banner image URL"
              />
            </div>
          </div>

          {/* Personal Details */}
          <div className="form-section">
            <h3 className="section-title">Personal Details</h3>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Gender</label>
                <select
                  name="gender"
                  className="form-input"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Language</label>
                <select
                  name="language"
                  className="form-input"
                  value={formData.language}
                  onChange={handleInputChange}
                >
                  <option value="english">English</option>
                  <option value="hinglish">Hinglish</option>
                  <option value="french">French</option>
                  <option value="german">German</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Visibility</label>
                <select
                  name="visibility"
                  className="form-input"
                  value={formData.visibility}
                  onChange={handleInputChange}
                >
                  <option value="PUBLIC">Public</option>
                  <option value="PRIVATE">Private</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">App Name</label>
                <input
                  type="text"
                  name="app_name"
                  className="form-input"
                  value={formData.app_name}
                  onChange={handleInputChange}
                  placeholder="Enter app name"
                />
              </div>
            </div>
          </div>

          {/* AI Model Settings */}
          <div className="form-section">
            <h3 className="section-title">AI Model Settings</h3>

            <div className="form-group">
              <label className="form-label">Model Name</label>
              <input
                type="text"
                name="model_name"
                className="form-input"
                value={formData.model_name}
                onChange={handleInputChange}
                placeholder="e.g., gpt-3.5-turbo"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Temperature (0-2)</label>
                <input
                  type="number"
                  name="temperature"
                  className="form-input"
                  value={formData.temperature}
                  onChange={handleInputChange}
                  min="0"
                  max="2"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Max Tokens</label>
                <input
                  type="number"
                  name="max_tokens"
                  className="form-input"
                  value={formData.max_tokens}
                  onChange={handleInputChange}
                  min="1"
                  max="4000"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Top P (0-1)</label>
                <input
                  type="number"
                  name="top_p"
                  className="form-input"
                  value={formData.top_p}
                  onChange={handleInputChange}
                  min="0"
                  max="1"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Top K</label>
                <input
                  type="number"
                  name="top_k"
                  className="form-input"
                  value={formData.top_k}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Welcome Experiment */}
          <div className="form-section">
            <h3 className="section-title">Welcome Experiment</h3>

            <div className="form-group">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleAddWelcomeExperiment}
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  color: 'white',
                }}
              >
                + Add Welcome Experiment
              </button>
            </div>

            {welcomeExperiments.map((experiment, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  padding: '15px',
                  marginBottom: '15px',
                  borderRadius: '5px',
                  position: 'relative',
                }}
              >
                <button
                  type="button"
                  onClick={() => handleRemoveWelcomeExperiment(index)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
                <div className="form-group">
                  <label className="form-label">Prompt</label>
                  <textarea
                    className="form-textarea"
                    value={experiment.prompt}
                    onChange={e =>
                      handleWelcomeExperimentChange(
                        index,
                        'prompt',
                        e.target.value
                      )
                    }
                    placeholder="Enter prompt"
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-textarea"
                    value={experiment.message}
                    onChange={e =>
                      handleWelcomeExperimentChange(
                        index,
                        'message',
                        e.target.value
                      )
                    }
                    placeholder="Enter message"
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Bucket List (comma-separated numbers)
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={experiment.bucket_list}
                    onChange={e =>
                      handleWelcomeExperimentChange(
                        index,
                        'bucket_list',
                        e.target.value
                      )
                    }
                    placeholder="e.g., 1, 2, 3"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Range Prompts */}
          <div className="form-section">
            <h3 className="section-title">Range Prompts</h3>

            <div className="form-group">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleAddRangePrompt}
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  color: 'white',
                }}
              >
                Add Range Prompt
              </button>
            </div>

            {rangePrompts.map((rangePrompt, index) => (
              <div
                key={index}
                className="form-row"
                style={{ alignItems: 'flex-end', gap: '10px' }}
              >
                <div className="form-group" style={{ flex: '0 0 80px' }}>
                  <label className="form-label">Min</label>
                  <input
                    type="text"
                    className="form-input"
                    value={rangePrompt.min}
                    onChange={e =>
                      handleRangePromptChange(index, 'min', e.target.value)
                    }
                    placeholder="Min"
                  />
                </div>
                <div className="form-group" style={{ flex: '0 0 80px' }}>
                  <label className="form-label">Max</label>
                  <input
                    type="text"
                    className="form-input"
                    value={rangePrompt.max}
                    onChange={e =>
                      handleRangePromptChange(index, 'max', e.target.value)
                    }
                    placeholder="Max"
                  />
                </div>
                <div className="form-group" style={{ flex: '1' }}>
                  <label className="form-label">Prompt</label>
                  <textarea
                    className="form-textarea"
                    value={rangePrompt.prompt}
                    onChange={e =>
                      handleRangePromptChange(index, 'prompt', e.target.value)
                    }
                    placeholder="Enter prompt text"
                    rows={3}
                  />
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => handleRemoveRangePrompt(index)}
                  style={{
                    marginBottom: '0',
                    height: 'fit-content',
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    color: 'white',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* Settings */}
          <div className="form-section">
            <h3 className="section-title">Settings</h3>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="nsfw"
                  checked={formData.nsfw}
                  onChange={handleInputChange}
                />
                NSFW Content
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {coach ? 'Update Coach' : 'Create Coach'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CoachModal;
